<template>
  <transition name="bg-modal">
    <div
      v-if="visible"
      class="bg-modal-overlay"
      role="presentation"
      @click.self="close"
    >
      <div
        class="bg-notice-modal bg-question-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bg-question-modal-title"
        @keydown.esc.prevent="close"
      >
        <header class="bg-notice-modal__header">
          <h2 id="bg-question-modal-title" class="bg-notice-modal__title">{{ title || $t('support.detailDefaultTitle') }}</h2>
          <button
            type="button"
            class="bg-notice-modal__close"
            :aria-label="$t('common.close')"
            @click="close"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        </header>

        <div class="bg-notice-modal__meta">
          <span :class="statusClass">{{ statusLabel }}</span>
          <span>{{ formattedDate }}</span>
        </div>

        <div class="bg-notice-modal__body bg-question-modal__body">
          <p v-if="loading" class="bg-notice-modal__status">{{ $t('common.loading') }}</p>
          <p v-else-if="loadError" class="bg-notice-modal__status bg-notice-modal__status--error">{{ loadError }}</p>
          <template v-else>
            <section class="bg-question-modal__block">
              <h3 class="bg-question-modal__block-title">{{ $t('support.detailQuestionTitle') }}</h3>
              <div
                class="bg-notice-modal__content"
                v-html="questionHtml"
              />
            </section>
            <section class="bg-question-modal__block">
              <h3 class="bg-question-modal__block-title">{{ $t('support.detailAnswerTitle') }}</h3>
              <div
                v-if="hasAnswer"
                class="bg-notice-modal__content bg-question-modal__answer"
                v-html="answerHtml"
              />
              <p v-else class="bg-question-modal__pending">{{ $t('support.detailAnswerPending') }}</p>
            </section>
          </template>
        </div>

        <footer class="bg-notice-modal__footer">
          <button type="button" class="bg-notice-modal__back" @click="close">{{ $t('common.backToList') }}</button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import { SystemService } from '@/services'
import { formatDateTime } from '@/utils/format'
import { getQuestionStatusLabel, toQuestionContentHtml } from '@/utils/question'

export default {
  name: 'BlackGoldQuestionDetailModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    questionIdx: {
      type: [Number, String],
      default: null
    }
  },
  data () {
    return {
      loading: false,
      loadError: '',
      title: '',
      questionHtml: '',
      answerHtml: '',
      regdate: '',
      status: 0
    }
  },
  computed: {
    formattedDate () {
      return formatDateTime(this.regdate)
    },
    hasAnswer () {
      return String(this.answerHtml || '').replace(/<br\s*\/?>/gi, '').trim().length > 0
    },
    statusLabel () {
      return getQuestionStatusLabel({
        status: this.status,
        answer: this.hasAnswer ? 'answered' : ''
      })
    },
    statusClass () {
      return this.hasAnswer || Number(this.status) === 1
        ? 'bg-question-modal__status--done'
        : 'bg-question-modal__status--pending'
    }
  },
  watch: {
    visible (open) {
      if (open && this.questionIdx != null) {
        this.loadDetail()
      }
      if (!open) {
        this.resetState()
      }
    },
    questionIdx (idx) {
      if (this.visible && idx != null) {
        this.loadDetail()
      }
    }
  },
  methods: {
    close () {
      this.$emit('close')
    },
    resetState () {
      this.loading = false
      this.loadError = ''
      this.title = ''
      this.questionHtml = ''
      this.answerHtml = ''
      this.regdate = ''
      this.status = 0
    },
    async loadDetail () {
      if (this.questionIdx == null || this.questionIdx === '') return
      this.loading = true
      this.loadError = ''
      this.title = ''
      this.questionHtml = ''
      this.answerHtml = ''
      try {
        const res = await SystemService.getQuestionDetail(this.questionIdx)
        if (!res) {
          this.loadError = this.$t('support.detailLoadError')
          return
        }
        if (res.data && res.data.code === 0 && res.data.data) {
          const data = res.data.data
          this.title = data.title || ''
          this.questionHtml = toQuestionContentHtml(data.question)
          this.answerHtml = toQuestionContentHtml(data.answer)
          this.regdate = data.regdate
          this.status = data.status
        } else {
          this.loadError = this.$t('support.detailLoadError')
        }
      } catch (err) {
        this.loadError = this.$t('support.detailLoadError')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
